/**
 * 居民小角色的纯模型（WORLD-RESIDENTS-ALIVE-117，6796）：公开居民此刻的状态 → 城市层上怎么演。只读服务端给的结构化字段，
 * 不从 doing 文字里猜，不编位置、不编动作。
 *
 * 三条规矩（I 10-15 认可的方案）：
 * - 在驿站：asleep 为 true 画「睡着」（窗灯变暗、z z z），false 画「醒着」（轻微待机），null 或缺了是说不清——只画「家」，不画站着的 TA；
 * - 在路上：只演「在路上」，位置按计划的出发、到达时刻算进度，沿两片区中心之间的虚线示意（只到片区，不画街道级路线）；没有计划到达时刻就原地踏步；
 * - 到访中、说不清：到访只说「在外面看看」（公开数据没有地点类别，不说在喝咖啡）；说不清就暗着、不画动作。
 * 动效全部由样式承担（PetMoodAvatar 与 actors.css），减少动态时全部静止；这里只给结构，不排帧。
 */
import type { PublicArea, PublicMapResident, PublicResidentMove } from "@/shared/contracts";
import type { PetMood } from "../PetMoodAvatar";

/** 城市层上的一幕：home_* 三种在驿站，road 在路上，place 到访中，unknown 说不清 */
export type ActorScene = "home_asleep" | "home_awake" | "home_unknown" | "road" | "place" | "unknown";

export function actorScene(resident: Pick<PublicMapResident, "presence" | "asleep">): ActorScene {
  switch (resident.presence) {
    case "at_home":
      return resident.asleep === true ? "home_asleep" : resident.asleep === false ? "home_awake" : "home_unknown";
    case "in_transit":
    case "returning":
      return "road";
    case "visiting":
    case "at_destination":
      return "place";
    default:
      return "unknown";
  }
}

/**
 * 这一幕借 PetMoodAvatar 的哪种表情；null＝不演表情（说不清睡没睡、说不清在哪），只画静态的 TA。
 * 到访用 exploring（四处看看）：公开数据没有地点类别，不拿 cafe（杯子冒热气）冒充「在店里坐着」。
 */
export function actorMood(scene: ActorScene): PetMood | null {
  switch (scene) {
    case "home_asleep":
      return "sleeping";
    case "home_awake":
      return "idle";
    case "road":
      return "walking";
    case "place":
      return "exploring";
    default:
      return null;
  }
}

/** 旁边挂哪个小标：在驿站挂「家」，到访挂「在外面」；在路上与说不清不挂 */
export function actorSign(scene: ActorScene): "home" | "place" | null {
  if (scene === "home_asleep" || scene === "home_awake" || scene === "home_unknown") return "home";
  if (scene === "place") return "place";
  return null;
}

const MINUTE = 60_000;

/**
 * 在路上这一程走到几成（0～1）：按计划的出发与到达时刻算，夹在 0～1；没有计划到达时刻、时刻读不出、到达不晚于出发，都是 null（原地踏步）。
 * 这是计划进度，不是实时位置——城市层只用它在两片区中心之间的示意虚线上放 TA。
 */
export function routeProgress(move: Pick<PublicResidentMove, "departed_at" | "arrive_eta"> | null | undefined, nowMs: number): number | null {
  if (!move || !move.arrive_eta || !Number.isFinite(nowMs)) return null;
  const start = Date.parse(move.departed_at);
  const end = Date.parse(move.arrive_eta);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end - start < MINUTE) return null;
  return Math.min(1, Math.max(0, (nowMs - start) / (end - start)));
}

export interface ScreenPoint {
  x: number;
  y: number;
}

/** 示意虚线上的一点：两端是页面用同一投影算好的片区中心屏幕坐标，进度为 null 时停在起点那头（原地踏步）。 */
export function routePoint(from: ScreenPoint, to: ScreenPoint, progress: number | null): ScreenPoint {
  const t = progress ?? 0;
  return { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t };
}

/** 在路上这一程的两头（片区中心）：只有两头都说得清才给；页面拿去投影成屏幕坐标再交给 ResidentRoute 与 routePoint */
export function routeEnds(resident: Pick<PublicMapResident, "presence" | "move">): { from: PublicArea; to: PublicArea } | null {
  if (actorScene({ presence: resident.presence, asleep: null }) !== "road" || !resident.move) return null;
  const { from_area: from, to_area: to } = resident.move;
  const ok = (a: PublicArea) => Number.isFinite(a.lat) && Number.isFinite(a.lng);
  return ok(from) && ok(to) ? { from, to } : null;
}
